import Button from "@/common/components/Button";
import Tabs from "@/common/components/Tabs";
import TextArea from "@/common/components/TextArea";
import TextField from "@/common/components/TextField";
import useCompany from "@/common/hooks/queries/useCompany";
import { formatCurrency } from "@/common/utils/formatters";
import useUpdateCompany from "@/features/privateCompanies/hooks/useUpdateCompany";
import { companySchema, CompanyValidationSchema } from "@/features/privateCompanies/schemas/create.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { InfoIcon } from "lucide-react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { SpecialEntries } from "../../components/SpecialEntries";
import { SubdomainSection } from "@/common/components/SubdomainSection";
import useDebounce from "@/common/hooks/useDebounce";
import { UserData } from "@/common/types/users";
import { Mapper } from "@/common/services/mapper";
import { useUsers } from "@/features/users/hooks/useUsers";
import SelectController from "@/common/components/SelectController";
import { useEffect } from "react";
import GoBackButton from "@/common/components/GoBackButton";
import { useGoBack } from "@/common/hooks/useGoBack";
import { FormTransformers } from "@/common/utils/formTransformers";

const Update = () => {
  const { id: companyId } = useParams();
  const queryClient = useQueryClient();
  const goBack = useGoBack();

  const { data: company, isFetched: companyIsFetched } = useCompany({ id: companyId || '' });

  const [searchUsers, setSearchUsers] = useDebounce('');

  const { data: usersOptions, isPending: usersIsPending } = useUsers({
    filters: {
      search: searchUsers,
    },
    mapper: Mapper.mapToOptions<UserData>({ valueFieldName: 'id', labelFieldName: 'full_name' }),
  });

  const { mutate: updateCompany, isPending: updateCompanyIsPending } = useUpdateCompany({
    companyId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      goBack({ fallback: `/companies/${companyId}` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError })(error);
    }
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors }
  } = useForm<CompanyValidationSchema>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      commercial_manager: {
        label: company?.commercial_manager?.full_name,
        value: company?.commercial_manager?.id,
      },
      commercial_manager_substitute: company?.commercial_manager_substitute ? {
        label: company?.commercial_manager_substitute.full_name,
        value: company?.commercial_manager_substitute.id,
      } : null,
      observations: company?.observations || '',
    },
  });

  const onSubmit: SubmitHandler<CompanyValidationSchema> = async (data) => {
    updateCompany(data);
  };

  useEffect(() => {
    if (companyIsFetched) {
      reset({
        commercial_manager: {
          label: company?.commercial_manager?.full_name,
          value: company?.commercial_manager?.id,
        },
        commercial_manager_substitute: company?.commercial_manager_substitute ? {
          label: company?.commercial_manager_substitute.full_name,
          value: company?.commercial_manager_substitute.id,
        } : null,
        observations: company?.observations || '',
      });
    }
  }, [companyIsFetched]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <GoBackButton fallback={`/companies/${companyId}`} />
          <h2 className="text-2xl font-bold">{company?.general_information?.fantasy_name}</h2>
        </div>
      </div>
      <Tabs
        tabsListClassName="w-full bg-transparent rounded-none"
        tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
        tabsContentClassName="mt-6"
        tabsList={[
          {
            label: 'Dados Gerais',
            value: '',
            disabled: true,
            body: (
              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col bg-white-100 rounded-md center p-4 gap-4 overflow-y-auto max-h-[calc(100vh-192px)] w-full">
                <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", company?.status === 'blocked' && 'bg-[#FCCDCA33]')}>
                  <div className="flex items-center gap-x-2">
                    <InfoIcon strokeWidth={1} className="text-yellow-600" />

                    <div>
                      <p className="font-bold text-[10px] leading-3">
                        Bloqueio da empresa impede todas transações
                      </p>

                      <p className="font-light text-[10px] italic leading-3">
                        Caso a empresa seja bloqueada, nenhuma transação poderá ser feita<br />pela empresa e o acesso ao sistema será revogado.
                      </p>
                    </div>
                  </div>

                  {company?.status !== 'blocked' ? (
                    <Button type="button" variant="error" disabled={true}>Bloquear empresa</Button>
                  ) : (
                    <Button
                      type="button"
                      disabled={true}
                    >
                      Desbloquear empresa
                    </Button>
                  )}
                </div>

                {company?.company_lead_id && (
                  <div className="flex w-full justify-between items-center bg-white-100 rounded-lg p-2">
                    <p className="font-bold text-lg">Detalhes da empresa</p>
                    <Button disabled>Visualizar</Button>
                  </div>
                )}
                <SpecialEntries
                  specialEntries={company?.fee_rule?.special_entries}
                  companyType={'establishment'}
                  disabled
                />

                <SubdomainSection url={company?.domains?.[0]?.host} />

                <div className="flex items-center justify-between">
                  <p className="text-[18px] font-bold">
                    Responsáveis internos
                  </p>

                  <div className="flex items-center gap-2">
                    <Button variant="outline-error" onClick={() => goBack({ fallback: `/companies/${companyId}` })} disabled={updateCompanyIsPending}>
                      Cancelar
                    </Button>

                    <Button
                      onClick={handleSubmit(onSubmit)}
                      isLoading={updateCompanyIsPending}
                      data-testid="submit_button"
                    >
                      Salvar
                    </Button>
                  </div>
                </div>

                <SelectController
                  control={control}
                  name="commercial_manager"
                  label="Responsável interno"
                  markAsRequired
                  errorMessage={errors?.commercial_manager?.message || undefined}
                  onInputChange={setSearchUsers}
                  options={usersOptions || []}
                  isLoading={usersIsPending}
                  isDisabled={updateCompanyIsPending}
                />

                <SelectController
                  control={control}
                  name="commercial_manager_substitute"
                  label="Responsável interno substituto"
                  errorMessage={errors?.commercial_manager_substitute?.message || undefined}
                  onInputChange={setSearchUsers}
                  options={usersOptions || []}
                  isClearable
                  isLoading={usersIsPending}
                  isDisabled={updateCompanyIsPending}
                />

                <TextArea
                  label="Observações"
                  rows={5}
                  maxLength={1500}
                  {...register('observations')}
                  errorMessage={errors.observations?.message || undefined}
                  disabled={updateCompanyIsPending}
                />

                <TextField
                  label="Gestor Master Frota"
                  value={company?.master_client_user?.full_name || 'Não informado'}
                  disabled
                />

                <div className="flex justify-between items-center gap-2">
                  <p className="text-xl">Resumo do contrato</p>

                  <div className="flex items-center gap-2">
                    <Button variant="success" disabled>Ver contrato</Button>
                  </div>
                </div>

                <TextField
                  label="Crédito garantido"
                  value={company?.granted_credit ? formatCurrency(parseFloat(company.granted_credit)) : 'Não informado'}
                  disabled
                />
                <TextField
                  label="Serviços"
                  value={company?.current_contract?.product_categories?.length ? company.current_contract.product_categories.map(category => category.display_name).join(', ') : 'Não informado'}
                  disabled
                />
                <TextField
                  label="Quantidade estimada de equipamentos"
                  value={company?.current_contract?.expected_equipment_quantity ? `${company.current_contract.expected_equipment_quantity}` : 'Não informado'}
                  disabled
                />
                <p className="text-sm">Atualmente {company?.equipments_count} cadastrados</p>
              </form>
            )
          },
          {
            label: 'Estrutura',
            value: 'structure',
            disabled: true,
            body: <></>,
          },
          {
            label: 'Equipamentos',
            value: 'equipments',
            disabled: true,
            body: <></>,
          },
          {
            label: 'Usuários',
            value: 'users',
            disabled: true,
            body: <></>,
          },
          {
            label: 'Transações',
            value: 'transactions',
            disabled: true,
            body: <></>,
          },
          {
            label: 'Totalizadores',
            value: 'totals',
            disabled: true,
            body: <></>,
          },
          {
            label: 'Faturas',
            value: 'invoices',
            disabled: true,
            body: <></>,
          },
        ]}
      />

    </div>
  );
};

export default Update;
